<template>
  <div class="promo-page">
    <!-- Header -->
    <header class="topbar">
      <h1 class="title">{{$t("Promotions")}}</h1>

      <div class="actions">
        <v-text-field
          :placeholder="$t('Izlash...')"
          append-icon="mdi-magnify"
          v-model.trim="query"
          dense
          hide-details
          outlined
          clearable
          class="search-input"
          style="max-width: 320px;"
          @click:clear="clearQuery"
        />

        <button v-if="canCreate" class="btn primary font-made" @click="openForm()">
          <v-icon small style="color:#000">mdi-plus</v-icon>
          <span style="font-weight: 600;">{{$t("New Promotion")}}</span>
        </button>
      </div>
    </header>

    <!-- Toolbar -->
    <section class="bar card">
      <div class="chip">
        <v-icon x-small>mdi-poll</v-icon>
        <span>{{ promotions.length }}</span>
      </div>

      <div class="sort">
        <span class="muted">{{$t("Sort")}}:</span>
        <button
          class="seg"
          :class="{active: sortKey==='id'}"
          @click="setSort('id')"
          title="Sort by ID"
        >
          <v-icon x-small>mdi-format-list-bulleted</v-icon>
          ID
          <v-icon x-small class="caret">{{ caret('id') }}</v-icon>
        </button>
        <button
          class="seg"
          :class="{active: sortKey==='promotion'}"
          @click="setSort('promotion')"
          title="Sort by Value"
        >
          <v-icon x-small>mdi-counter</v-icon>
          {{$t("Value")}}
          <v-icon x-small class="caret">{{ caret('promotion') }}</v-icon>
        </button>
      </div>
    </section>

    <!-- Table / Empty / Loading -->
    <section class="card">
      <div v-if="loading" class="skeleton">
        <div v-for="i in 6" :key="i" class="row"></div>
      </div>

      <template v-else>
        <div v-if="!paginated.length" class="empty">
          <div class="empty-art"><v-icon large>mdi-file-outline</v-icon></div>
          <h3>No promotions</h3>
          <p class="muted">Try another search or create a new one.</p>
          <button v-if="canCreate" class="btn primary font-made" @click="openForm()">
            {{$t("New Promotion")}}
          </button>
        </div>

        <table v-else class="table">
          <thead>
            <tr>
              <th class="left" @click="setSort('id')">
                ID <v-icon x-small class="th-caret">{{ caret('id') }}</v-icon>
              </th>
              <th class="center">{{$t("Promotion Name")}}</th>
              <th class="right" @click="setSort('promotion')">
                {{$t("Promotion Value")}} <v-icon x-small class="th-caret">{{ caret('promotion') }}</v-icon>
              </th>
              <th class="center">{{$t("Type")}}</th>
              <th class="right">{{$t("Actions")}}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in paginated" :key="p.id">
              <td class="left">#{{ p.id }}</td>
              <td class="right">{{ p.p_name }}</td>
              <td class="right">
                <span class="emph">{{ p.isPercent ? (p.promotion + '%') : formatNumber(p.promotion) }}</span>
              </td>
              <td class="center">
                <span class="tag" :class="p.isPercent ? 'green' : 'indigo'">
                  <v-icon x-small class="tag-ic" style="color: #374151;" v-if="p.isPercent">mdi-percent-outline</v-icon>
                  <v-icon x-small class="tag-ic" style="color: #000;" v-else>mdi-cash</v-icon>
                  <span>{{ p.isPercent ? $t("In Percent") : $t("So'mda") }}</span>
                </span>
              </td>
              <td class="right">
                <div class="row-actions">
                  <button v-if="canUpdate" class="tc-act" @click="openForm(p)" :title="$t('Tahrirlash')">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                  </button>
                  <button v-if="canDelete" class="tc-act tc-act--delete" @click="askDelete(p)" :title="$t('Delete')">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="pageCount>1" class="pa-3 d-flex justify-center">
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="7"
          />
        </div>
      </template>
    </section>

    <!-- Form Modal -->
    <div v-if="showForm" class="overlay" @click.self="closeForm">
      <div class="modal">
        <div class="modal-head">
          <h3>{{ editing ? $t('Edit Promotion') : $t('New Promotion') }}</h3>
        </div>

        <div class="field">
          <label>{{$t("Type")}}</label>
          <div class="toggle">
            <button type="button" :class="{on: !form.isPercent}" @click="form.isPercent=false">
              <v-icon x-small>mdi-cash</v-icon> 
              <span style="color: #000;">{{$t("So'mda")}}</span>
            </button>
            <button type="button" :class="{on: form.isPercent}" @click="form.isPercent=true">
              <v-icon x-small>mdi-percent-outline</v-icon> {{$t("In Percent")}}
            </button>
          </div>
        </div>

        <div class="field">
          <label for="value">{{$t("Promotion Name")}}</label>
          <div class="affix" :class="{'err': error}">
            <input
                id="value"
                ref="value"
                v-model.number="form.name"
                type="text"
                :step="1"
                :placeholder="$t('Enter name')"
            />
          </div>
        </div>

        <div class="field">
          <label for="value">{{$t("Promotion Value")}}</label>
          <div class="affix" :class="{'err': error}">
            <input
              id="value"
              ref="value"
              v-model.number="form.promotion"
              type="number"
              :min="0"
              :max="form.isPercent ? 100 : null"
              :step="1"
              :placeholder="$t('Enter value')"
            />
            <span class="suf">{{ form.isPercent ? '%' : 'UZS' }}</span>
          </div>
          <small v-if="error" class="error">{{ error }}</small>
        </div>

        <div class="modal-actions">
          <button class="btn ghost" @click="closeForm">
            <v-icon small>mdi-close</v-icon> {{$t("Cancel")}}
          </button>
          <button class="btn primary" :disabled="saving || !isValid" @click="savePromotion">
            <svg v-if="saving" class="spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke-width="2" stroke-linecap="round" stroke-dasharray="14 8"/>
            </svg>
            <template v-else>
              <v-icon small style="color:#000">mdi-check</v-icon> {{ editing ? $t('Save') : $t('Create') }}
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="confirmDelete" class="overlay" @click.self="confirmDelete=false">
      <div class="modal confirm">
        <div class="warn"><v-icon>mdi-alert</v-icon></div>
        <h3>Delete promotion #{{ toDelete?.id }}?</h3>
        <p class="muted">This cannot be undone.</p>
        <div class="modal-actions">
          <button class="btn ghost" @click="confirmDelete=false">
            <v-icon small>mdi-close</v-icon> Cancel
          </button>
          <button class="btn danger" :disabled="deleting" @click="performDelete">
            <svg v-if="deleting" class="spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke-width="2" stroke-linecap="round" stroke-dasharray="14 8"/>
            </svg>
            <template v-else>
              <v-icon small >mdi-check</v-icon> <span style="color: #000;">Delete</span>
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Toasts -->
    <div class="toasts">
      <div v-for="t in toasts" :key="t.id" class="toast" :class="t.type">
        <v-icon v-if="t.type==='success'">mdi-check-circle</v-icon>
        <v-icon v-else-if="t.type==='error'">mdi-alert-circle</v-icon>
        <v-icon v-else>mdi-information</v-icon>
        <span v-html="t.msg"></span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PromotionPage",
  data() {
    return {
      canCreate: false,
      canUpdate: false,
      canDelete: false,
      promotions: [],
      loading: false,

      // list controls
      query: "",
      sortKey: "id",
      sortDir: "desc",
      page: 1,
      pageSize: 100,

      // form
      showForm: false,
      editing: false,
      saving: false,
      form: { id: null, promotion: null, isPercent: false },
      error: "",

      // delete
      confirmDelete: false,
      deleting: false,
      toDelete: null,

      // toasts
      toasts: [],
      nextToastId: 1,
    };
  },
  async mounted() {
    this.canCreate = await this.hasRole("CHEGIRMA_SOZLASH", "CREATE");
    this.canUpdate = await this.hasRole("CHEGIRMA_SOZLASH", "UPDATE");
    this.canDelete = await this.hasRole("CHEGIRMA_SOZLASH", "DELETE");
  },
  created() {
    this.loadPromotions();
    // lightweight shortcut: press "/" to focus search (if not typing)
    this._onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (!/INPUT|TEXTAREA|SELECT/.test(tag) && e.key === "/") {
        e.preventDefault();
        this.$refs.search && this.$refs.search.focus();
      }
    };
    window.addEventListener("keydown", this._onKey);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this._onKey);
  },
  computed: {
    filtered() {
      const q = (this.query || "").toLowerCase();
      let list = this.promotions.slice();

      // sort
      const dir = this.sortDir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        const av = this.sortKey === "id" || this.sortKey === "promotion"
          ? Number(a[this.sortKey] || 0)
          : String(a[this.sortKey] || "");
        const bv = this.sortKey === "id" || this.sortKey === "promotion"
          ? Number(b[this.sortKey] || 0)
          : String(b[this.sortKey] || "");
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
      });

      if (!q) return list;
      return list.filter(p =>
        String(p.id).includes(q) ||
        String(p.promotion).toLowerCase().includes(q)
      );
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
    },
    paginated() {
      const start = (this.page - 1) * this.pageSize;
      return this.filtered.slice(start, start + this.pageSize);
    },
    isValid() {
      const v = this.form.promotion;
      if (v === null || v === "" || isNaN(v)) return false;
      if (v < 0) return false;
      if (this.form.isPercent && (v < 0 || v > 100)) return false;
      return true;
    }
  },
  watch: {
    filtered() { if (this.page > this.pageCount) this.page = this.pageCount; },
    query() { this.page = 1; },
    "form.isPercent"(v) {
      if (v && this.form.promotion > 100) this.form.promotion = 100;
      if (this.form.promotion < 0) this.form.promotion = 0;
      this.validate();
    },
    "form.promotion"() { this.validate(); }
  },
  methods: {
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
    caret(key) {
      if (this.sortKey !== key) return "mdi-swap-vertical";
      return this.sortDir === "asc" ? "mdi-chevron-up" : "mdi-chevron-down";
    },
    async loadPromotions() {
      this.loading = true;
      try {
        const res = await axios.get("/admin/promotions");
        this.promotions = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.toast("Failed to load promotions.", "error");
      } finally {
        this.loading = false;
      }
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    openForm(p = null) {
      this.editing = !!p;
      this.form = p ? { ...p } : { id: null, promotion: null, isPercent: false };
      this.error = "";
      this.showForm = true;
      this.$nextTick(() => this.$refs.value && this.$refs.value.focus());
    },
    closeForm() {
      if (this.saving) return;
      this.showForm = false;
      this.form = { id: null, promotion: null, isPercent: false };
      this.error = "";
    },
    validate() {
      const v = this.form.promotion;
      this.error = "";
      if (v === null || v === "" || isNaN(v)) this.error = "Please enter a value.";
      else if (v < 0) this.error = "Value cannot be negative.";
      else if (this.form.isPercent && (v < 0 || v > 100)) this.error = "Percent must be 0–100.";
    },
    async savePromotion() {
      this.validate();
      if (!this.isValid) return;

      this.saving = true;
      try {
        if (this.editing) {
          await axios.put(`/admin/promotions/${this.form.id}`, this.form);
          this.toast(`Promotion <b>#${this.form.id}</b> updated.`, "success");
        } else {
          await axios.post("/admin/promotions", this.form);
          this.toast("Promotion created.", "success");
        }
        this.closeForm();
        await this.loadPromotions();
      } catch (e) {
        this.toast("Save failed. Please try again.", "error");
      } finally {
        this.saving = false;
      }
    },
    askDelete(p) {
      this.toDelete = p;
      this.confirmDelete = true;
    },
    async performDelete() {
      if (!this.toDelete) return;
      this.deleting = true;
      try {
        await axios.delete(`/admin/promotions/${this.toDelete.id}`);
        this.toast(`Promotion <b>#${this.toDelete.id}</b> deleted.`, "success");
        this.confirmDelete = false;
        this.toDelete = null;
        await this.loadPromotions();
      } catch (e) {
        this.toast("Delete failed.", "error");
      } finally {
        this.deleting = false;
      }
    },
    clearQuery() {
      this.query = "";
      this.$refs.search && this.$refs.search.focus();
    },
    formatNumber(n) {
      try { return Number(n).toLocaleString("en-US") + " " + "UZS"; } catch { return n; }
    },
    toast(msg, type = "success", ttl = 2800) {
      const id = this.nextToastId++;
      this.toasts.push({ id, msg, type });
      setTimeout(() => {
        const i = this.toasts.findIndex(t => t.id === id);
        if (i !== -1) this.toasts.splice(i, 1);
      }, ttl);
    }
  }
};
</script>

<style lang="scss" scoped>
/* ===== Theme (no CSS variables) */
.promo-page { max-width: 1024px; margin: 36px auto 72px; padding: 0 16px; color: #0f172a; }

/* ===== Header */
.topbar { display:flex; gap:12px; align-items:center; justify-content:space-between; margin-bottom:14px; }
.title { font-size:22px; font-weight:800; letter-spacing:.2px; margin:0; }
.actions { display:flex; gap:10px; align-items:center; }
::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}

/* ===== Toolbar */
.card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow: none; overflow: hidden; }
.bar { display:flex; justify-content:space-between; align-items:center; padding:12px 14px; margin-bottom:12px; }
.chip { display:inline-flex; align-items:center; gap:8px; padding:6px 10px; border-radius:999px; background:#eef2ff; color:#1e3a8a; font-weight:700; font-size:12px; }
.sort { display:flex; align-items:center; gap:8px; }
.muted { color:#6b7280; }
.bold { font-weight:800; }
.seg {
  border:1px solid #e5e7eb; background:#fff; border-radius:10px; padding:6px 10px; font-size:12.5px; font-weight:700; display:inline-flex; gap:6px; align-items:center;
}
.seg.active { border-color:#ffd451; background:#fffbeb; box-shadow:none; }
.caret { color:#6b7280; }

/* ===== Table */
.section { overflow:hidden; }
.table { width:100%; border-collapse:separate; border-spacing:0; }
.table thead th {
  position:sticky; top:0; z-index:1; background:#fafafb; color:#6b7280; font-size:12px; text-transform:uppercase; letter-spacing:.6px;
  font-weight:800; padding:12px 12px; border-bottom:1px solid #e5e7eb; user-select:none; cursor:pointer;
}
.table thead th:last-child { cursor:default; }
.table tbody td { padding:12px; border-bottom:1px solid #eef1f5; font-size:14.5px; }
.table tbody tr { transition: background .12s; }
.table tbody tr:hover { background:#fbfdff; }
.left{text-align:left} .right{text-align:right} .center{text-align:center}
.emph { font-weight:800; color:#0f172a; }

.tag { display:inline-flex; align-items:center; gap:8px; padding:6px 10px; border-radius:999px; font-weight:800; font-size:12px; }
.tag-ic { font-size:14px; }
.tag.indigo { background:#ffd451 !important; color:#000 !important; }
.tag.indigo .tag-ic { color:#000 !important; }
.tag.green  { background:#f3f4f6 !important; color:#374151 !important; }
.tag.green .tag-ic { color:#374151 !important; }

.row-actions { display:flex; gap:6px; justify-content:flex-end; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

/* ===== Buttons */
.btn { border:0; border-radius:12px; padding:9px 12px; font-size:14px; font-weight:800; display:inline-flex; gap:8px; align-items:center; cursor:pointer; }
.btn.primary { color:#000 !important; background:#ffd451 !important; box-shadow:none !important; }
.btn.primary:hover { background:#f5c542 !important; box-shadow:none !important; }
.btn.ghost { background:#fff; border:1px solid #e5e7eb; color:#111827; }
.btn.danger { color:#fff; background:linear-gradient(135deg, #ef4444, #dc2626); box-shadow:none; }
.spin { animation:spin .9s linear infinite; width:18px; height:18px; }
.spin circle { stroke: currentColor; }
@keyframes spin { to{ transform:rotate(360deg); } }

/* ===== Overlay & Modals */
.overlay { position:fixed; inset:0; background:rgba(2,6,23,.5); display:grid; place-items:center; padding:16px; z-index:50; }
.modal {
  width:480px; max-width:100%; background:#fff; border:1px solid #e5e7eb; border-radius:16px; box-shadow:0 24px 64px rgba(0,0,0,.12);
  padding:16px; animation:pop .16s ease-out forwards; transform:translateY(6px) scale(.98); opacity:0;
}
@keyframes pop { to{ transform:translateY(0) scale(1); opacity:1; } }
.modal-head { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.dot { width:32px; height:32px; border-radius:10px; background:#eef2ff; display:grid; place-items:center; }
.field { margin:12px 0; }
.field label { display:block; font-weight:800; font-size:12.5px; color:#374151; margin-bottom:6px; }
.toggle { display:flex; gap:8px; }
.toggle button {
  border:1px solid #e5e7eb; background:#fff; border-radius:10px; padding:8px 12px; font-weight:800; font-size:13px; display:inline-flex; gap:6px; align-items:center;
}
.toggle button.on {
  border: 2px solid #f5c542;
  background: #ffd451;
  box-shadow: none;
  color: #000;
}
.affix { position:relative; }
.affix input {
  width:100%; padding:11px 44px 11px 12px; border:1px solid #e5e7eb; border-radius:12px; font-size:15px; outline:none;
}
.affix input:focus { border-color:#00274c; border-width: 2px; padding:10px 43px 10px 11px; }
.affix .suf { position:absolute; right:10px; top:50%; transform:translateY(-50%); color:#6b7280; font-weight:800; }
.affix.err input { border-color:#ef4444; border-width: 2px; padding:10px 43px 10px 11px; }
.error { color:#dc2626; font-weight:700; margin-top:6px; display:inline-block; }
.modal-actions { display:flex; justify-content:flex-end; gap:8px; margin-top:14px; }
.confirm { text-align:center; }
.warn { width:54px; height:54px; border-radius:16px; background:#fff3f3; display:grid; place-items:center; margin:0 auto 8px; }

/* ===== Empty & Skeleton */
.empty { padding:32px 12px; display:grid; place-items:center; gap:8px; text-align:center; }
.empty h3 { margin:4px 0 0; }
.empty-art { width:72px; height:72px; border-radius:20px; background:#eef2ff; display:grid; place-items:center; }
.skeleton { padding:10px; }
.skeleton .row {
  height:50px; border-bottom:1px solid #eef1f5;
  background:linear-gradient(90deg, #f6f7fb 25%, #eef1f6 37%, #f6f7fb 63%);
  background-size:400% 100%; animation:shimmer 1.2s infinite;
}
@keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:0 0} }

/* ===== Toasts */
.toasts { position:fixed; right:14px; bottom:14px; display:grid; gap:8px; z-index:60; }
.toast {
  display:flex; align-items:center; gap:8px; background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:9px 12px; min-width:220px;
  box-shadow: none; font-weight:700; font-size:12.5px; color:#0b1220;
}
.toast.success { color:#065f46; background:#ecfdf5; border-color:#d1fae5; }
.toast.error { color:#7f1d1d; background:#fef2f2; border-color:#fee2e2; }

/* ===== Responsive */
@media (max-width: 760px) {
  .topbar { flex-direction:column; align-items:stretch; }
  .actions { justify-content:space-between; }
}

/* Keep icons black when toggle is active */
.toggle button.on .v-icon,
.toggle button.on span {
  color: #000 !important;
}

@media (max-width: 600px) {
  .promo-page .card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .promo-page .table { min-width: 640px; }
}
</style>
